import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { RetailerPicker } from "@/components/admin/RetailerPicker";
import api from "@/lib/api";
import { toast } from "sonner";
import { Rss, RefreshCw, Loader2, Package, PackageX, Search, Trash2, Save, SlidersHorizontal, Clock } from "lucide-react";

const fmtDate = (v) =>
  v ? new Date(v).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" }) : "Never";

export const RetailerFeed = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState("");
  const [discOpen, setDiscOpen] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [curate, setCurate] = useState(null); // { retailer, data, selected }

  const openCurate = async (r, refresh = false) => {
    setCurate({ retailer: r, data: null, selected: r.feed_categories || [] });
    try {
      const res = await api.get(`/admin/retailers/${r.advertiser_id}/feed-categories`,
        { params: refresh ? { refresh: true } : {} });
      setCurate({ retailer: r, data: res.data, selected: res.data.selected || [] });
    } catch (e) {
      setCurate({ retailer: r, data: { categories: [], error: e.response?.data?.detail || "Could not read the feed" }, selected: [] });
    }
  };

  const toggleCat = (name) => setCurate((c) => ({
    ...c,
    selected: c.selected.includes(name) ? c.selected.filter((x) => x !== name) : [...c.selected, name],
  }));

  const saveCurate = () => run(`cur-${curate.retailer.advertiser_id}`, async () => {
    try {
      await api.put(`/admin/retailers/${curate.retailer.advertiser_id}`,
        { feed_categories: curate.selected });
      toast.success(curate.selected.length
        ? `Importing ${curate.selected.length} categories from ${curate.retailer.advertiser_name}`
        : `Importing all categories from ${curate.retailer.advertiser_name}`);
      setCurate(null);
    } catch { toast.error("Could not save categories"); }
  });

  const load = () =>
    api.get("/admin/feed/status").then((r) => setStatus(r.data)).catch(() => {});

  useEffect(() => { load(); }, []);

  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); } finally { setBusy(""); load(); }
  };

  const syncAll = () => run("all", async () => {
    try {
      const r = await api.post("/admin/feed/sync");
      const errs = r.data.errors?.length ? ` · ${r.data.errors.length} failed` : "";
      toast.success(`Imported ${r.data.imported} products from ${r.data.retailers_synced} retailers${errs}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); }
  });

  const syncOne = (id) => run(`s-${id}`, async () => {
    try {
      const r = await api.post(`/admin/feed/sync/${id}`);
      const res = r.data.results?.[0];
      toast.success(`Imported ${r.data.imported} products`
        + (res?.removed ? ` · ${res.removed} removed` : ""));
    } catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); }
  });

  const simulate = () => run("sim", async () => {
    try {
      const r = await api.post("/admin/feed/simulate");
      toast.success(`Refreshed ${r.data.updated} seeded products`);
    } catch { toast.error("Simulated refresh failed"); }
  });

  const patch = (id, body) => run(`p-${id}`, async () => {
    try { await api.put(`/admin/retailers/${id}`, body); }
    catch { toast.error("Could not update retailer"); }
  });

  const remove = (id) => run(`d-${id}`, async () => {
    try {
      const r = await api.delete(`/admin/retailers/${id}`);
      toast.success(`Removed · ${r.data.products_removed} products deleted`);
    } catch { toast.error("Could not remove retailer"); }
  });

  const setDraft = (id, k, v) => setDrafts((d) => ({ ...d, [id]: { ...d[id], [k]: v } }));

  if (!status) return <Loader2 className="w-5 h-5 animate-spin" data-testid="feed-loading" />;

  const live = status.source === "awin";

  return (
    <div className="space-y-6" data-testid="admin-retailer-feed">
      <div className="bg-white rounded-2xl border border-brand-cream2 p-6">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
          <div>
            <h3 className="font-serif text-xl flex items-center gap-2">
              <Rss className="w-5 h-5 text-brand-amber" /> Product feeds
            </h3>
            <div className="flex items-center gap-2 mt-2">
              <Badge data-testid="feed-source-badge"
                className={live ? "bg-brand-green text-brand-cream" : "bg-brand-amber/20 text-brand-ink border border-brand-amber/40"}>
                {live ? "Awin — live" : "Simulated — fallback"}
              </Badge>
              <span className="text-xs text-muted-foreground">Last sync {fmtDate(status.last_run)}</span>
              <span className="text-xs text-muted-foreground inline-flex items-center gap-1" data-testid="feed-auto-sync">
                <Clock className="w-3 h-3" /> Auto-syncs hourly
              </span>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setDiscOpen(true)}
              data-testid="feed-discover-button" className="rounded-full gap-2">
              <Search className="w-4 h-4" /> Find retailers
            </Button>
            <Button onClick={syncAll} disabled={busy === "all" || !status.awin_configured}
              data-testid="feed-sync-all-button" className="bg-brand-green hover:bg-brand-greendark rounded-full gap-2">
              {busy === "all" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Sync now
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <FeedStat icon={Rss} label="Awin products" value={status.awin_products} testid="feed-stat-awin" />
          <FeedStat icon={Package} label="Seeded products" value={status.seeded_products} testid="feed-stat-seeded" />
          <FeedStat icon={Package} label="Live on site" value={status.total} testid="feed-stat-total" />
          <FeedStat icon={PackageX} label="Out of stock" value={status.out_of_stock} testid="feed-stat-oos" />
        </div>

        <div className="flex items-center justify-between gap-4 mt-5 pt-5 border-t border-brand-cream2">
          <div>
            <Label className="text-sm">Show only Awin products on the site</Label>
            <p className="text-xs text-muted-foreground mt-0.5">Hides the seeded starter catalogue once Awin coverage is good enough.</p>
          </div>
          <Switch checked={status.hide_seeded} data-testid="feed-hide-seeded-switch"
            onCheckedChange={(v) => run("hide", async () => {
              await api.put("/admin/feed/settings", { hide_seeded: v });
              toast.success(v ? "Seeded catalogue hidden" : "Seeded catalogue visible");
            })} />
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-brand-cream2 overflow-hidden">
        <div className="p-5 border-b border-brand-cream2">
          <h3 className="font-serif text-lg">Connected retailers</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Each sync imports giftable products inside the price range, up to the cap.
          </p>
        </div>
        {status.retailers.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground" data-testid="feed-no-retailers">
            No retailers connected yet. Use “Find retailers” to see the Awin programmes this account is approved for.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Retailer</TableHead>
                  <TableHead>Products</TableHead>
                  <TableHead>Price range (£)</TableHead>
                  <TableHead>Cap</TableHead>
                  <TableHead>Last sync</TableHead>
                  <TableHead>On</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {status.retailers.map((r) => {
                  const d = drafts[r.advertiser_id] || {};
                  const min = d.min_price ?? r.min_price;
                  const max = d.max_price ?? r.max_price;
                  const cap = d.cap ?? r.cap;
                  const dirty = Number(min) !== r.min_price || Number(max) !== r.max_price || Number(cap) !== r.cap;
                  return (
                    <TableRow key={r.advertiser_id} data-testid={`retailer-row-${r.advertiser_id}`}>
                      <TableCell>
                        <p className="font-medium">{r.advertiser_name}</p>
                        <p className="text-xs text-muted-foreground font-mono">#{r.advertiser_id}</p>
                        <button onClick={() => openCurate(r)} data-testid={`retailer-categories-${r.advertiser_id}`}
                          className="text-xs text-brand-green underline decoration-dotted mt-1 inline-flex items-center gap-1">
                          <SlidersHorizontal className="w-3 h-3" />
                          {r.feed_categories?.length ? `${r.feed_categories.length} categories picked` : "All categories"}
                        </button>
                        {r.last_sync_error && !r.last_sync && (
                          <p className="text-xs text-brand-burgundy mt-1 max-w-[220px]">{r.last_sync_error}</p>
                        )}
                      </TableCell>
                      <TableCell className="font-mono">{r.product_count}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Input type="number" value={min} className="w-20 h-8"
                            data-testid={`retailer-min-${r.advertiser_id}`}
                            onChange={(e) => setDraft(r.advertiser_id, "min_price", e.target.value)} />
                          <span className="text-muted-foreground">–</span>
                          <Input type="number" value={max} className="w-20 h-8"
                            data-testid={`retailer-max-${r.advertiser_id}`}
                            onChange={(e) => setDraft(r.advertiser_id, "max_price", e.target.value)} />
                        </div>
                      </TableCell>
                      <TableCell>
                        <Input type="number" value={cap} className="w-20 h-8"
                          data-testid={`retailer-cap-${r.advertiser_id}`}
                          onChange={(e) => setDraft(r.advertiser_id, "cap", e.target.value)} />
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(r.last_sync)}</TableCell>
                      <TableCell>
                        <Switch checked={r.enabled !== false} data-testid={`retailer-enabled-${r.advertiser_id}`}
                          onCheckedChange={(v) => patch(r.advertiser_id, { enabled: v })} />
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {dirty && (
                          <Button variant="ghost" size="icon" title="Save filters"
                            data-testid={`retailer-save-${r.advertiser_id}`}
                            onClick={() => patch(r.advertiser_id, {
                              min_price: Number(min), max_price: Number(max), cap: Number(cap),
                            })}>
                            <Save className="w-4 h-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" title="Sync this retailer"
                          disabled={busy === `s-${r.advertiser_id}`}
                          data-testid={`retailer-sync-${r.advertiser_id}`}
                          onClick={() => syncOne(r.advertiser_id)}>
                          {busy === `s-${r.advertiser_id}`
                            ? <Loader2 className="w-4 h-4 animate-spin" />
                            : <RefreshCw className="w-4 h-4" />}
                        </Button>
                        <Button variant="ghost" size="icon" className="text-brand-burgundy" title="Remove"
                          data-testid={`retailer-remove-${r.advertiser_id}`}
                          onClick={() => remove(r.advertiser_id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <div className="bg-brand-amber/10 border border-brand-amber/30 rounded-xl p-4 text-sm text-brand-ink/80 flex flex-wrap items-center justify-between gap-3"
        data-testid="feed-note">
        <span>{status.note}</span>
        <Button variant="outline" size="sm" onClick={simulate} disabled={busy === "sim"}
          data-testid="feed-simulate-button" className="rounded-full gap-2">
          {busy === "sim" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Refresh simulated prices
        </Button>
      </div>

      <RetailerPicker open={discOpen} onOpenChange={setDiscOpen}
        connected={status.retailers} onChanged={load} />
      <Dialog open={!!curate} onOpenChange={(v) => !v && setCurate(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="curate-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              Giftable categories — {curate?.retailer?.advertiser_name}
            </DialogTitle>
            <DialogDescription>
              Pick the parts of this retailer's catalogue that actually work as gifts.
            </DialogDescription>
          </DialogHeader>
          {!curate?.data ? <Loader2 className="w-5 h-5 animate-spin" /> : curate.data.error ? (
            <p className="text-sm text-brand-burgundy" data-testid="curate-error">{curate.data.error}</p>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                {curate.data.feed_rows} rows in this feed · {curate.data.importable_products} importable ·
                {" "}{curate.data.currencies?.join(", ")}
                {curate.data.min_price != null ? ` · £${curate.data.min_price}–£${curate.data.max_price}` : ""}.
                {curate.data.has_supplied_categories
                  ? " Tick the categories worth importing; leave everything unticked to import the lot."
                  : " This retailer supplies no categories, so these are worked out from product titles."}
              </p>
              {curate.data.message && (
                <p className="text-sm text-brand-burgundy mt-2" data-testid="curate-message">{curate.data.message}</p>
              )}
              <div className="grid sm:grid-cols-2 gap-2 mt-2">
                {curate.data.categories.map((c) => {
                  const on = curate.selected.includes(c.name);
                  return (
                    <button key={c.name} onClick={() => toggleCat(c.name)}
                      data-testid={`curate-cat-${c.name.replace(/\s+/g, "-").toLowerCase()}`}
                      className={`flex items-center justify-between gap-3 text-left px-3 py-2 rounded-xl border transition-colors ${
                        on ? "border-brand-green bg-brand-green/10" : "border-brand-cream2 hover:bg-brand-cream2/50"}`}>
                      <span className="text-sm">
                        {c.name}
                        {c.derived && <span className="text-[11px] text-muted-foreground ml-2">worked out</span>}
                      </span>
                      <span className="font-mono text-xs text-muted-foreground">{c.count}</span>
                    </button>
                  );
                })}
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="rounded-full"
                    data-testid="curate-clear" onClick={() => setCurate({ ...curate, selected: [] })}>
                    Clear
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-full gap-2"
                    data-testid="curate-refresh" onClick={() => openCurate(curate.retailer, true)}>
                    <RefreshCw className="w-3.5 h-3.5" /> Re-read feed
                  </Button>
                </div>
                <Button onClick={saveCurate} data-testid="curate-save"
                  className="bg-brand-green hover:bg-brand-greendark rounded-full gap-2">
                  <Save className="w-4 h-4" /> Save categories
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

function FeedStat({ icon: Icon, label, value, testid }) {
  return (
    <div className="bg-brand-cream2/50 rounded-xl p-4" data-testid={testid}>
      <Icon className="w-4 h-4 text-brand-amber mb-2" />
      <p className="font-mono font-bold text-brand-green text-2xl">{value}</p>
      <p className="text-xs text-muted-foreground mt-1">{label}</p>
    </div>
  );
}
